import { RequestError } from "../requestError";
import api from "../axiosBase";
import { getURL } from "../serviceHelper";
import { getEnv } from "../../AppConfigs";
import { getFileExt, getFullDocumentUrl } from "../util";
import { documentStatus } from "../../constants/statusConstants";
import { IFileDocumentFields, IFileDocumentInfo } from "../../models/IFileDocumentInfo";
import axios from "../department-viags/config.service";
import { pageIndex, pageSize } from "../../constants/page";

const SPSiteId = getEnv("REACT_APP_SP_SITE_ID");
const SPListId = getEnv("REACT_APP_WORKING_DOCS_INF_LIST_ID");
const SPHisListId = getEnv("REACT_APP_WORKING_DOCS_HIS_INF_LIST_ID");
const SPPublishListId = getEnv("REACT_APP_WORKING_DOCS_PUBLISH_INF_LIST_ID");
const SPSiteUrl = getEnv("REACT_APP_SHAREPOINT_SITE_URL");

const expand =
  "&expand=fields(select=Title,docWorkingId,workingFileId,docStatusId,IsConvertToPDF,IsCustomEditor,docStatusValue,subInfo,docStatus,docNotes,effectiveFrom,effectiveTo,docId,publishDate,publishVersion,publishViewVersion,docUrl,docLinks,chatGroupId,chatGroupName,docTypeLookupId,docType,docAssignedTo,docSharedTo,docEditors,docReviewers,docDetectors,docCheckers,docPublicEndUsers,approvedBy, approvedName, approvedDate,fileId,fileAssignedTo,docPublicVersion,docEditionVersion,docPublishViewVersion,docPublicDepartment,docPublicDepartmentLookupId,docCode,Tags,docOrder,docAreas, docReaders,isMigrate, Modified,docEditTemp, fileAttachment, isContentPermission)";

export class DocumentService {
  static async searchPublicDocuments(keyword: string) {
    const { data } = await axios.get(`/edoc/search-issued-docs`, {
      params: { keyword, pageIndex, pageSize },
    });
    return data.data;
  }
  static async searchDocuments(keyword: string) {
    const { data } = await axios.get(`/edoc/search-docs`, {
      params: { keyword, pageIndex, pageSize },
    });
    return data.data;
  }

  static async getDocumentInfo(docId: any, listId: any = undefined) {
    if (!listId) listId = SPListId;

    return api
      .get(getURL(`/sites/${SPSiteId}/lists/${listId}/items?${expand}&filter=fields/docId eq ${docId}`))
      .then((res) => {
        if (res && res.data.value.length > 0) return this.mapData(res.data.value[0]);
        return null;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getHistoryDocumentInfo(docId: any, listId: any = SPPublishListId) {
    return api.get(getURL(`/sites/${SPSiteId}/lists/${listId}/items/${docId}/versions`));
  }

  static async get(id: any, listId: any = undefined) {
    if (!listId) listId = SPListId;

    return api
      .get(getURL(`/sites/${SPSiteId}/lists/${listId}/items/${id}?${expand}`))
      .then((res) => {
        return this.mapData(res.data);
      })
      .catch((err: any) => {
        if (err.message.indexOf("404") > -1) return null;
        throw new RequestError(err);
      });
  }

  static async getListDocumentInfo() {
    return api
      .get(getURL(`/sites/${SPSiteId}/lists/${SPListId}/items?${expand}&$top=5000`))
      .then((res) => {
        const child: [] = res.data.value.map((item: any) => DocumentService.mapData(item));
        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }
  static async getListPublicDocumentInfo() {
    const allItems: any[] = [];
    let nextLink = getURL(`/sites/${SPSiteId}/lists/${SPPublishListId}/items?${expand}&$top=5000`);

    try {
      while (nextLink) {
        const res = await api.get(nextLink);
        const currentItems = res.data.value.map((item: any) => DocumentService.mapData(item));
        allItems.push(...currentItems);

        // Continue if there's a nextLink
        nextLink = res.data["@odata.nextLink"] || null;
      }

      var filtered = allItems.filter(s => !s.IsConvertToPDF && s.createdUser.email !== "botit@viags.vn")

      return {
        data: filtered,
        count: filtered.length,
      };
    } catch (err) {
      throw new RequestError(err);
    }
  }

  static async getDocumentInfoFromWorkingDate(effectiveFrom: any, effectiveTo: any, listId: any = SPListId) {
    return api
      .get(
        getURL(
          `/sites/${SPSiteId}/lists/${listId}/items?${expand}&$top=5000&filter=(fields/effectiveFrom ge '${effectiveFrom}' and fields/effectiveFrom le '${effectiveTo}') or (fields/effectiveTo ge '${effectiveTo}' and fields/effectiveFrom le '${effectiveTo}')`,
          // `/sites/${SPSiteId}/lists/${SPListId}/items?${expand}&$top=5000`
        ),
      )
      .then((res) => {
        const child = res.data.value.map((item: any) => DocumentService.mapData(item));
        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getPublicDocumentInfoFromWorkingDocument(docWorkingId: any, listId: any = undefined) {
    return api
      .get(
        getURL(
          `/sites/${SPSiteId}/lists/${SPPublishListId}/items?${expand}&filter=fields/docWorkingId eq ${docWorkingId}`,
        ),
      )
      .then((res) => {
        if (res && res.data.value.length > 0) return this.mapData(res.data.value[0]);
        return null;
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getPublicExpiredDocumentInfoFromWorkingDocument(dateExpired: any, listId: any = undefined) {
    return api
      .get(
        getURL(
          `/sites/${SPSiteId}/lists/${SPPublishListId}/items?${expand}&$top=5000&filter=fields/effectiveTo le '${dateExpired}'`,
        ),
      )
      .then((res) => {
        const child = res.data.value.map((item: any) => DocumentService.mapData(item));
        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getWorkingDocuments(email?: string, status?: string) {
    let url = "";
    switch (status) {
      case documentStatus.compose.value:
        url = `/sites/${SPSiteId}/lists/${SPListId}/items?${expand}&$top=5000&filter=(fields/docStatusId lt '${documentStatus.readytopublish.id}' or fields/docStatusId eq '${documentStatus.reject.id}')`;
        break;
      case documentStatus.approval.value:
        url = `/sites/${SPSiteId}/lists/${SPListId}/items?${expand}&$top=5000&filter=(fields/docStatusId eq '${documentStatus.approval.id}')`;
        break;
      default:
        break;
    }
    return api
      .get(getURL(url))
      .then((res) => {
        const result = res.data.value.map((item: any) => DocumentService.mapData(item));
        /* const result = child.filter(
          (o: any) => o.fileAssignedTo.indexOf(email.toLowerCase() || "") > -1
        ); */
        return {
          data: result,
          count: result.length,
        };
      })
      .catch((err) => {
        console.log(err);
        throw new RequestError(err);
      });
  }

  static async getWorkingDocumentsByTitle(email: string, title?: string) {
    let url = "";

    if (title) {
      url = `/sites/${SPSiteId}/lists/${SPListId}/items?${expand}&filter=startswith(fields/Title, '${title}')&$top=5000`;
    }

    return api
      .get(getURL(url))
      .then((res) => {
        const result = res.data.value.map((item: any) => DocumentService.mapData(item));
        /* const result = child.filter(
          (o: any) => o.fileAssignedTo.indexOf(email.toLowerCase() || "") > -1
        ); */
        return {
          data: result,
          count: result.length,
        };
      })
      .catch((err) => {
        console.log(err);
        throw new RequestError(err);
      });
  }

  static async getFileHistoryInfo(docId: string): Promise<any[]> {
    const url = getURL(`/sites/${SPSiteId}/lists/${SPHisListId}/items?${expand}&filter=fields/docId eq ${docId}`);
    const res = await api.get(url);
    return res.data.value;
  }

  static async getPublishDocument(docTypeId?: number, keyword?: string) {
    let url = `/sites/${SPSiteId}/lists/${SPPublishListId}/items?${expand}&$top=5000&filter=`;
    if (docTypeId && keyword) {
      url += `fields/docTypeId eq ${+docTypeId} and startswith(fields/Title,'${keyword}')`;
    } else {
      if (docTypeId) {
        url += `fields/docTypeId eq ${+docTypeId}`;
      }
      if (keyword) {
        url += `startswith(fields/Title,'${keyword}')`;
      }
    }

    return api
      .get(getURL(url))
      .then((res) => {
        const child = res.data.value.map((item: any) => DocumentService.mapData(item));
        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getDocumentByListId(ids: string[]) {
    if (ids.length === 0) return { data: [], count: 0 };
    let url = `/sites/${SPSiteId}/lists/${SPListId}/items?${expand}&$top=5000&filter=`;
    const stringFiterId = ids.join(" or fields/docId eq ");
    url += `fields/docId eq ${stringFiterId}`;
    return api
      .get(getURL(url))
      .then((res) => {
        const child = res.data.value.map((item: any) => DocumentService.mapData(item));
        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }
  static async getPublishDocumentByListId(ids: string[]) {
    if (ids.length === 0) return { data: [], count: 0 };
    let url = `/sites/${SPSiteId}/lists/${SPPublishListId}/items?${expand}&$top=5000&filter=`;
    const stringFiterId = ids.join(" or fields/docWorkingId eq ");
    url += `fields/docWorkingId eq ${stringFiterId}`;
    return api
      .get(getURL(url))
      .then((res) => {
        const child = res.data.value.map((item: any) => this.mapData(item));
        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async createFileInfo(fields: any, listId?: any) {
    if (!listId) listId = SPListId;

    return api
      .post(getURL(`/sites/${SPSiteId}/lists/${listId}/items`), { fields })
      .then((res) => this.mapData(res.data))
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updateFileInfo(id: any, fields: any, listId?: any) {
    if (!listId) listId = SPListId;

    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${listId}/items/${id}/fields`), fields)
      .then((res) => {
        if (!res) return null;
        return this.mapDataFromUpdate(res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async updatePulishFileInfo(id: any, fields: any) {
    return api
      .patch(getURL(`/sites/${SPSiteId}/lists/${SPPublishListId}/items/${id}/fields`), fields)
      .then((res) => {
        if (!res) return null;
        return this.mapDataFromUpdate(res.data);
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static mapDataFromUpdate(documentItem: any) {
    return {
      id: documentItem.id,
      fileId: documentItem.fileId,
      listItemId: documentItem.docId,
      Title: documentItem.Title,
      type: documentItem.docUrl ? getFileExt(documentItem.docUrl) : "",
      docUrl: documentItem.docUrl ? getFullDocumentUrl(documentItem.docUrl, SPSiteUrl) : "",
      docId: documentItem.docId,
      docType: documentItem.docType,
      docLinks: documentItem.docLinks,
      docStatusId: documentItem.docStatusId,
      docStatus: documentItem.docStatus,
      docStatusValue: documentItem.docStatusValue,
      docTypeId: documentItem.docTypeLookupId,
      docNotes: documentItem.docNotes,
      subInfo: documentItem.subInfo,
      approvedBy: documentItem.approvedBy,
      approvedName: documentItem.approvedName,
      approvedDate: documentItem.approvedDate,
      effectiveFrom: documentItem.effectiveFrom,
      effectiveTo: documentItem.effectiveTo,
      fileAssignedTo: documentItem.fileAssignedTo || "",
      docEditors: documentItem.docEditors || "",
      docReviewers: documentItem.docReviewers ? JSON.parse(documentItem.docReviewers) : undefined,
      docDetectors: documentItem.docDetectors ? JSON.parse(documentItem.docDetectors) : undefined,
      docCheckers: documentItem.docCheckers ? JSON.parse(documentItem.docCheckers) : undefined,
      docPublicEndUsers: documentItem.docPublicEndUsers ? JSON.parse(documentItem.docPublicEndUsers) : undefined,
      chatGroupId: documentItem.chatGroupId,
      chatGroupName: documentItem.chatGroupName,
      createdBy: documentItem.createdBy,
      createdDate: documentItem.createdDate ?? documentItem.createdDateTime,
      docPublicVersion: documentItem.docPublicVersion,
      docEditionVersion: documentItem.docEditionVersion,
      docPublishViewVersion: documentItem.docPublishViewVersion,
      docPublicDepartment: documentItem.docPublicDepartment,
      docPublicDepartmentLookupId: documentItem.docPublicDepartmentLookupId,
      docCode: documentItem.docCode,
      docOrder: documentItem.docOrder,
      Tags: documentItem.Tags ? JSON.parse(documentItem.Tags) : [],
      docAreas: documentItem.docAreas ? JSON.parse(documentItem.docAreas) : [],
      IsConvertToPDF: documentItem.IsConvertToPDF,
      docWorkingId: documentItem?.docWorkingId,
      workingFileId: documentItem?.workingFileId,
      IsCustomEditor: documentItem?.IsCustomEditor,
      docReaders: documentItem?.docReaders,
      docEditTemp: documentItem?.docEditTemp,
      isSendMail: documentItem?.isSendMail,
      isContentPermission: documentItem?.isContentPermission,
      fileAttachment: documentItem?.fileAttachment,
    } as IFileDocumentInfo;
  }

  static mapData(documentItem: any) {
    return {
      id: documentItem.id,
      fileId: documentItem.fields?.fileId,
      listItemId: documentItem.fields?.docId,
      Title: documentItem.fields?.Title,
      createdDateTime: documentItem.createdDateTime,
      createdUser: documentItem.createdBy?.user,
      modifiedUser: documentItem.lastModifiedBy?.user,
      lastModifiedDateTime: documentItem.lastModifiedDateTime,
      type: documentItem.fields?.docUrl ? getFileExt(documentItem.fields?.docUrl) : "",
      docUrl: documentItem.fields?.docUrl ? getFullDocumentUrl(documentItem.fields?.docUrl, SPSiteUrl) : "",
      docId: documentItem.fields?.docId,
      docType: documentItem.fields?.docType,
      docLinks: documentItem.fields?.docLinks,
      docStatusId: documentItem.fields?.docStatusId,
      docStatus: documentItem.fields?.docStatus,
      docStatusValue: documentItem.fields?.docStatusValue,
      docTypeId: documentItem.fields?.docTypeLookupId,
      docNotes: documentItem.fields?.docNotes,
      subInfo: documentItem.fields?.subInfo,
      approvedBy: documentItem.fields?.approvedBy,
      approvedName: documentItem.fields?.approvedName,
      approvedDate: documentItem.fields?.approvedDate,
      effectiveFrom: documentItem.fields?.effectiveFrom,
      effectiveTo: documentItem.fields?.effectiveTo,
      fileAssignedTo: documentItem.fields?.fileAssignedTo || "",
      docEditors: documentItem.fields?.docEditors || "",
      docReviewers: documentItem.fields?.docReviewers ? JSON.parse(documentItem.fields?.docReviewers) : undefined,
      docDetectors: documentItem.fields?.docDetectors ? JSON.parse(documentItem.fields?.docDetectors) : undefined,
      docCheckers: documentItem.fields?.docCheckers ? JSON.parse(documentItem.fields?.docCheckers) : undefined,
      docPublicEndUsers: documentItem.fields?.docPublicEndUsers
        ? JSON.parse(documentItem.fields?.docPublicEndUsers)
        : undefined,
      chatGroupId: documentItem.fields?.chatGroupId,
      chatGroupName: documentItem.fields?.chatGroupName,
      createdDate: documentItem.fields?.createdDate ?? documentItem.createdDateTime,
      docPublicVersion: documentItem.fields?.docPublicVersion,
      docEditionVersion: documentItem.fields?.docEditionVersion,
      docPublishViewVersion: documentItem.fields?.docPublishViewVersion,
      docPublicDepartment: documentItem.fields?.docPublicDepartment,
      docPublicDepartmentLookupId: documentItem.fields?.docPublicDepartmentLookupId,
      docCode: documentItem.fields?.docCode,
      docOrder: documentItem.fields?.docOrder,
      userDisplayName: documentItem.createdBy?.user?.displayName,
      userEmail: documentItem.createdBy?.user?.email,
      Tags: documentItem.fields?.Tags ? JSON.parse(documentItem.fields?.Tags) : [],
      docAreas: documentItem.fields?.docAreas ? JSON.parse(documentItem.fields?.docAreas) : [],
      IsConvertToPDF: documentItem.fields?.IsConvertToPDF,
      docWorkingId: documentItem?.fields?.docWorkingId,
      workingFileId: documentItem?.fields?.workingFileId,
      IsCustomEditor: documentItem?.fields?.IsCustomEditor,
      webUrl: documentItem?.webUrl,
      docReaders: documentItem?.fields?.docReaders ? JSON.parse(documentItem?.fields?.docReaders) : undefined,
      docEditTemp: documentItem?.fields?.docEditTemp,
      isSendMail: documentItem?.fields?.isSendMail,
      isContentPermission: documentItem?.fields?.isContentPermission,
      fileAttachment: documentItem.fields?.fileAttachment ? JSON.parse(documentItem.fields?.fileAttachment) : undefined,
    } as IFileDocumentInfo;
  }

  static mapDocumentInfoToFIelds(documentInfo: IFileDocumentInfo) {
    return {
      fileId: documentInfo?.fileId,
      docId: documentInfo?.docId,
      Title: documentInfo?.Title,
      docUrl: documentInfo?.docUrl,
      docTypeLookupId: documentInfo?.docTypeLookupId,
      docStatusId: documentInfo?.docStatusId,
      docStatus: documentInfo?.docStatus,
      docStatusValue: documentInfo?.docStatusValue,
      docNotes: documentInfo?.docNotes,
      subInfo: documentInfo?.subInfo,
      approvedBy: documentInfo?.approvedBy,
      approvedName: documentInfo?.approvedName,
      approvedDate: documentInfo?.approvedDate,
      effectiveFrom: documentInfo?.effectiveFrom,
      effectiveTo: documentInfo?.effectiveTo,
      fileAssignedTo: documentInfo?.fileAssignedTo,
      docEditors: documentInfo?.docEditors,
      docReviewers: documentInfo?.docReviewers ? JSON.stringify(documentInfo?.docReviewers) : undefined,
      docDetectors: documentInfo?.docDetectors ? JSON.stringify(documentInfo?.docDetectors) : undefined,
      docCheckers: documentInfo?.docCheckers ? JSON.stringify(documentInfo?.docCheckers) : undefined,
      docPublicEndUsers: documentInfo?.docPublicEndUsers ? JSON.stringify(documentInfo?.docPublicEndUsers) : undefined,
      chatGroupId: documentInfo?.chatGroupId,
      chatGroupName: documentInfo?.chatGroupName,
      docPublicVersion: documentInfo?.docPublicVersion,
      docPublishViewVersion: documentInfo?.docPublishViewVersion,
      docEditionVersion: documentInfo?.docEditionVersion,
      docPublicDepartmentLookupId: documentInfo?.docPublicDepartmentLookupId,
      docCode: documentInfo?.docCode,
      docOrder: documentInfo?.docOrder,
      Tags: documentInfo?.Tags ? JSON.stringify(documentInfo?.Tags) : undefined,
      docAreas: documentInfo?.docAreas ? JSON.stringify(documentInfo?.docAreas) : undefined,
      IsConvertToPDF: documentInfo?.IsConvertToPDF,
      docWorkingId: documentInfo?.docWorkingId,
      workingFileId: documentInfo?.workingFileId,
      IsCustomEditor: documentInfo?.IsCustomEditor,
      publishedDate: documentInfo?.publishedDate,
      isDeleted: documentInfo?.isDeleted,
      docReaders: documentInfo?.docReaders ? JSON.stringify(documentInfo?.docReaders) : null,
      docEditTemp: documentInfo?.docEditTemp,
      isSendMail: documentInfo?.isSendMail,
      isContentPermission: documentInfo?.isContentPermission,
      fileAttachment: documentInfo?.fileAttachment ? JSON.stringify(documentInfo?.fileAttachment) : null,
    } as IFileDocumentFields;
  }
}
